/**
 * @copyright WaterStreet. All rights reserved.
 */

import {
	Component,
	ElementRef,
	EventEmitter,
	OnDestroy,
	OnInit,
	Output,
	ViewChild
} from '@angular/core';
import {
	FieldType,
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';

@Component({
	selector: 'custom-section-title',
	templateUrl: './custom-section-title.component.html',
	styleUrls: ['./custom-section-title.component.scss']
})

/**
 * A component representing an instance of a Custom Section Title.
 * https://ngx-formly.github.io/ngx-formly/guide
 *
 * @export
 * @class CustomSectionTitleComponent
 * @extends {FieldType}
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
export class CustomSectionTitleComponent
	extends FieldType
	implements OnInit, OnDestroy
{
	/**
	 * Gets or sets the tooltip element reference.
	 *
	 * @type {ElementRef}
	 * @memberof CustomSectionTitleComponent
	 */
	@ViewChild('Tooltip')
	public tooltip: ElementRef;

	/**
	 * Sets the event emitter action to communicate to
	 * implementing components that the add button was
	 * clicked.
	 *
	 * @type {EventEmitter}
	 * @memberof CustomSectionTitleComponent
	 */
	@Output() public addAction: EventEmitter<void> =
		new EventEmitter();

	/**
	 * Sets the event emitter action to communicate to
	 * implementing components that the load all button was
	 * clicked.
	 *
	 * @type {EventEmitter<void>}
	 * @memberof CustomSectionTitleComponent
	 */
	@Output() public displayAllEvent: EventEmitter<void> =
		new EventEmitter();

	/**
	 * Gets or sets the interval that is used to limit the number of times
	 * an item can be added per second. This allows repeater displays and
	 * defaults sufficient time to complete.
	 *
	 * @type {NodeJS.Timeout}
	 * @memberof CustomSectionTitleComponent
	 */
	public addItemInterval: NodeJS.Timeout;

	/**
	 * Gets or sets the thruthy to determine if the section title is
	 * at a top position at the layout.
	 *
	 * @type {boolean}
	 * @memberof CustomSectionTitleComponent
	 */
	public topSectionTitle: boolean;

	/**
	 * Cleans and reutrns the current section title label
	 * value. This value is set as an id which is used
	 * for functions such as scrollTo throughout the site.
	 *
	 * @returns {string}
	 * The cleaned identifier to be used as an id for this
	 * section title.
	 * @memberof CustomSectionTitleComponent
	 */
	public get cleanLabelIdentifier(): string
	{
		return StringHelper.getCleanedValue(
			this.field.props.label) + this.sectionIdentifier;
	}

	/**
	 * Gets or sets the array that represents the existence of an item to be
	 * added.
	 *
	 * @type {number[]}
	 * @memberof CustomSectionTitleComponent
	 */
	private readonly addItemTasks: number[] = [];

	/**
	 * Gets or sets the name added to section title ids for tab
	 * interaction.
	 *
	 * @type {string}
	 * @memberof CustomSectionTitleComponent
	 */
	private readonly sectionIdentifier: string = 'Section';

	/**
	 * On initialization event.
	 * Adds a delayed interval watcher for requests to add an item.
	 *
	 * @memberof CustomSectionTitleComponent
	 */
	public ngOnInit(): void
	{
		this.topSectionTitle = this.isTopSectionTitle();

		if (AnyHelper.isNull(this.field.fieldGroup)
			|| this.field.props.disabled === true)
		{
			return;
		}

		this.setAddItemInterval();
	}

	/**
	 * On destroy event.
	 * Clears the add item subscription and the interval.
	 *
	 * @memberof CustomSectionTitleComponent
	 */
	public ngOnDestroy(): void
	{
		clearInterval(this.addItemInterval);
	}

	/**
	 * Calculates and returns the count of displayed items in this repeater.
	 *
	 * @returns {number}
	 * The count of all items in a repeater section that have a display value
	 * of true.
	 * @memberof CustomSectionTitleComponent
	 */
	public getRepeaterItemCount(): number
	{
		 return this.field.fieldGroup.filter(
			 (field: FormlyFieldConfig) =>
				 field.props.display === true).length;
	}

	/**
	 * Calculates and returns the count of virtually displayed items in this
	 * repeater.
	 *
	 * @returns {number}
	 * The count of all items in a repeater section that have a virtual display
	 * value of true.
	 * @memberof CustomSectionTitleComponent
	 */
	public getRepeaterVirtualItemCount(): number
	{
		return this.field.fieldGroup.filter(
			(field: FormlyFieldConfig) =>
				field.props.virtualDisplay === true).length;
	}

	/**
	 * Emits the add action event to all listening components.
	 * This signifies that the add button has been clicked.
	 *
	 * @memberof CustomSectionTitleComponent
	 */
	public addItemAction(): void
	{
		this.addItemTasks.push(1);
	}

	/**
	 * Emits the item count change action event to all listening components.
	 * This signifies that the load all button has been clicked and sends the
	 * new virtual display count.
	 *
	 * @memberof CustomSectionTitleComponent
	 */
	public displayAll(): void
	{
		this.displayAllEvent.emit();
	}

	/**
	 * Sets the add item interval.
	 *
	 * @memberof CustomSectionTitleComponent
	 */
	public setAddItemInterval(): void
	{
		this.addItemInterval =
			setInterval(
				() =>
				{
					if (this.addItemTasks.length > 0)
					{
						this.addItemTasks.pop();
						this.addAction.emit();
					}
				},
				AppConstants.time.quarterSecond);
	}

	/**
	 * Gets the top section title definition.
	 * Returns true if this is the first section title on the page OR
	 * if this is the first section title within a custom-nested-tab-content wrapper.
	 *
	 * @memberof CustomSectionTitleComponent
	 */
	private isTopSectionTitle(): boolean
	{
		// Check if this is the first field on the page
		const isPageTopSection: boolean = !AnyHelper.isNull(this.field.props.attributes)
			&& this.field.props.attributes[
				FormlyConstants.attributeKeys.fieldIndex] === 0;

		if (isPageTopSection)
		{
			return true;
		}

		// Check if this is the first section title within a custom-nested-tab-content wrapper
		return this.isFirstSectionInNestedTabContent();
	}

	/**
	 * Determines if this section title is the first section title within
	 * a custom-nested-tab-content wrapper.
	 *
	 * @returns {boolean}
	 * True if this is the first section title within a nested tab content wrapper.
	 * @memberof CustomSectionTitleComponent
	 */
	private isFirstSectionInNestedTabContent(): boolean
	{
		// Check if the parent field has a custom-nested-tab-content wrapper
		const parentField: FormlyFieldConfig = this.field.parent;
		if (AnyHelper.isNull(parentField?.wrappers)
			|| !parentField.wrappers.includes('custom-nested-tab-content'))
		{
			return false;
		}

		// Check if this is the first custom-section-title in the parent's fieldGroup
		if (!Array.isArray(parentField.fieldGroup)
			|| AnyHelper.isNullOrEmptyArray(parentField.fieldGroup))
		{
			return false;
		}

		// Find the first custom-section-title field in the parent's fieldGroup
		const firstSectionTitle: FormlyFieldConfig = parentField.fieldGroup.find(field => 
			field.type === 'custom-section-title');

		// Return true if this field is the first section title found
		return firstSectionTitle === this.field;
	}
}